import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { isAdmin, ROLES } from '../../utils/permissions';
import { Users, Search, Edit2, Trash2, Shield, X, UserPlus } from 'lucide-react';

export default function UserManagement({ user, profile }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ displayName: '', email: '', role: 'staff', active: true });

  useEffect(() => {
    if (!profile?.companyId) return;
    const unsub = onSnapshot(
      collection(db, 'companies', profile.companyId, 'users'),
      snap => {
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to users:', error);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [profile?.companyId]);

  const admin = isAdmin(profile?.role);

  const filtered = users.filter(u =>
    (u.displayName || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      displayName: u.displayName || '',
      email: u.email || '',
      role: u.role || 'staff',
      active: u.active !== false,
    });
    setShowModal(true);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ displayName: '', email: '', role: 'staff', active: true });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!profile?.companyId) return;
    try {
      if (editing) {
        await updateDoc(doc(db, 'companies', profile.companyId, 'users', editing.id), {
          displayName: form.displayName,
          role: form.role,
          active: form.active,
        });
      } else {
        // Adding a new user record (they'll need to register or be invited)
        const tempId = `invited_${Date.now()}`;
        await setDoc(doc(db, 'companies', profile.companyId, 'users', tempId), {
          displayName: form.displayName,
          email: form.email,
          role: form.role,
          active: form.active,
          invited: true,
          invitedBy: user.uid,
          invitedAt: new Date().toISOString(),
        });
      }
      setShowModal(false);
    } catch (err) {
      console.error('Save user error:', err);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Remove ${u.displayName || u.email} from the company?`)) return;
    try {
      await deleteDoc(doc(db, 'companies', profile.companyId, 'users', u.id));
    } catch (err) {
      console.error('Delete user error:', err);
    }
  };

  const roleColors = {
    admin: 'bg-red-100 text-red-700',
    manager: 'bg-blue-100 text-blue-700',
    staff: 'bg-green-100 text-green-700',
    viewer: 'bg-gray-100 text-gray-700',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users size={24} /> User Management
          </h1>
          <p className="text-gray-400 mt-1">{users.length} team members</p>
        </div>
        {admin && (
          <button
            onClick={openAdd}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-lg font-medium hover:bg-primary-dark transition-colors"
          >
            <UserPlus size={18} /> Add User
          </button>
        )}
      </div>

      <div className="relative mb-6">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search users..."
          className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      </div>

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-white/5 border-b border-white/10">
              <th className="text-left px-5 py-3 text-sm font-medium text-gray-400">Name</th>
              <th className="text-left px-5 py-3 text-sm font-medium text-gray-400">Email</th>
              <th className="text-left px-5 py-3 text-sm font-medium text-gray-400">Role</th>
              <th className="text-left px-5 py-3 text-sm font-medium text-gray-400">Status</th>
              {admin && <th className="text-right px-5 py-3 text-sm font-medium text-gray-400">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-b border-white/10 last:border-0 hover:bg-white/5">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">
                      {(u.displayName || u.email || '?')[0].toUpperCase()}
                    </div>
                    <span className="font-medium text-white">{u.displayName || '—'}</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-gray-400">{u.email}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[u.role] || roleColors.staff}`}>
                    <Shield size={10} /> {u.role}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-medium ${u.active !== false ? 'text-green-600' : 'text-red-600'}`}>
                    {u.active !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                {admin && (
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(u)} className="text-gray-400 hover:text-primary">
                        <Edit2 size={16} />
                      </button>
                      {u.id !== user.uid && (
                        <button onClick={() => handleDelete(u)} className="text-gray-400 hover:text-red-600">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={admin ? 5 : 4} className="px-5 py-10 text-center text-gray-400">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-white/10 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">
                {editing ? 'Edit User' : 'Add User'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-300">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Display Name</label>
                <input
                  type="text"
                  value={form.displayName}
                  onChange={e => setForm({ ...form, displayName: e.target.value })}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white"
                />
              </div>
              {!editing && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
                <select
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white"
                >
                  {Object.keys(ROLES).map(r => (
                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={e => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4 rounded text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-300">Active</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 bg-white/10 border border-white/20 text-gray-300 py-2.5 rounded-lg font-medium hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 bg-primary text-white py-2.5 rounded-lg font-medium hover:bg-primary-dark"
              >
                {editing ? 'Save Changes' : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
